import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  AccountBalance as BankIcon,
  CompareArrows as ReconcileIcon,
} from '@mui/icons-material';
import { checksAPI } from '../services/api';
import CheckFormModal from '../components/CheckFormModal';
import CheckReconcileModal from '../components/CheckReconcileModal';
import { useAuth } from '../contexts/AuthContext';

const statusColors = {
  draft: 'default',
  pending: 'warning',
  cleared: 'success',
  bounced: 'error',
  cancelled: 'default',
  Confirmé: 'success',
  confirmed: 'success',
};

const statusLabels = {
  draft: 'Brouillon',
  pending: 'En attente',
  cleared: 'Encaissé',
  bounced: 'Impayé',
  cancelled: 'Annulé',
  Confirmé: 'Confirmé',
  confirmed: 'Confirmé',
};

const directionColors = {
  incoming: 'success',
  outgoing: 'error',
};

const directionLabels = {
  incoming: 'Entrant',
  outgoing: 'Sortant',
};

// Helper function to check if check is in final status (non-editable)
const isFinalStatus = (check) => {
  if (check.status === 'cleared' && !check.reconciled) {
    return false;
  }
  return ['cleared', 'bounced', 'cancelled', 'Confirmé', 'confirmed'].includes(check.status);
};

function ChecksPage() {
  const { user } = useAuth();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);

  useEffect(() => {
    loadChecks();
  }, []);

  const loadChecks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (directionFilter !== 'all') params.direction = directionFilter;
      const response = await checksAPI.getAll(params);
      setChecks(Array.isArray(response.data.results) ? response.data.results : Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error('Error loading checks:', err);
      setError('Échec du chargement des chèques. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chèque?')) return;
    try {
      await checksAPI.delete(id);
      loadChecks();
    } catch (err) {
      console.error('Error deleting check:', err);
      alert('Échec de la suppression.');
    }
  };

  const handleEdit = (check) => {
    setEditingCheck(check);
    setFormModalOpen(true);
  };

  const handleCreate = () => {
    setEditingCheck(null);
    setFormModalOpen(true);
  };

  const handleModalClose = (saved = false) => {
    setFormModalOpen(false);
    setEditingCheck(null);
    if (saved) loadChecks();
  };

  const filteredChecks = Array.isArray(checks) ? checks.filter((check) => {
    const term = searchTerm.toLowerCase();
    return (
      (check.check_number?.toLowerCase().includes(term) ||
        check.bank_name?.toLowerCase().includes(term) ||
        check.beneficiary?.toLowerCase().includes(term)) ?? false
    );
  }) : [];

  const paginatedChecks = filteredChecks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Chèques
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadChecks}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReconcileIcon />}
            onClick={() => setReconcileModalOpen(true)}
          >
            Réconciliation bancaire
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nouveau chèque
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Tous</MenuItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Direction</InputLabel>
            <Select
              value={directionFilter}
              label="Direction"
              onChange={(e) => setDirectionFilter(e.target.value)}
            >
              <MenuItem value="all">Tous</MenuItem>
              {Object.entries(directionLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>N° de chèque</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Banque</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date d'émission</TableCell>
                <TableCell>Date d'échéance</TableCell>
                <TableCell>Bénéficiaire</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedChecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Aucun chèque
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedChecks.map((check) => (
                  <TableRow key={check.id} hover>
                    <TableCell>
                      {check.proof_image_url ? (
                        check.proof_image_url.toLowerCase().endsWith('.pdf') ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(check.proof_image_url, '_blank');
                            }}
                          >
                            <FileIcon fontSize="small" color="error" />
                          </IconButton>
                        ) : (
                          <img
                            src={check.proof_image_url}
                            alt=""
                            style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', border: '1px solid #ddd', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (check.proof_image_url) window.open(check.proof_image_url, '_blank');
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )
                      ) : (
                        <ImageIcon fontSize="small" color="disabled" />
                      )}
                    </TableCell>
                    <TableCell>{check.check_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={directionLabels[check.direction] || check.direction}
                        color={directionColors[check.direction] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{check.bank_name || '-'}</TableCell>
                    <TableCell>
                      {check.amount?.toLocaleString?.()} {check.currency}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[check.status] || check.status}
                        color={statusColors[check.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{check.issue_date}</TableCell>
                    <TableCell>{check.due_date || '-'}</TableCell>
                    <TableCell>{check.beneficiary || '-'}</TableCell>
                    <TableCell align="center">
                      {!isFinalStatus(check) && (
                        <>
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => handleEdit(check)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" color="error" onClick={() => handleDelete(check.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isFinalStatus(check) && (
                        <Typography variant="caption" color="text.secondary">
                          Verrouillé
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredChecks.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Lignes/page"
        />
      </Paper>

      <CheckFormModal
        open={formModalOpen}
        onClose={handleModalClose}
        check={editingCheck}
      />
      <CheckReconcileModal
        open={reconcileModalOpen}
        onClose={() => { setReconcileModalOpen(false); loadChecks(); }}
      />

    </Container>
  );
}

export default ChecksPage;
